import {
  GATEWAY_ROUTE_GSMU_ENDPOINT,
  GATEWAY_ROUTE_GUID,
  GATEWAY_ROUTE_LIBRARIES,
  GATEWAY_ROUTE_MCP_PORT,
  GATEWAY_ROUTE_PROJECT,
  GATEWAY_ROUTE_REGISTRY,
  GATEWAY_ROUTE_WEBSOCKET_PORT,
} from '@/core/const/gatewayRoutes.const'
import type { Library } from '@/core/types/library.type'
import { fetchJson, fetchText } from '@/core/utils/fetch'

export interface GatewayConfig {
  host: string
  port?: string
  isSecured: boolean
}

export interface LibrariesPayload {
  libraries: Library[]
  guid: string
  registry: string
  projectName: string
  gsmuEndpoint: string
  websocketPort: string
  mcpPort: string
}

/**
 * Builds gateway base URL. Relative paths (e.g. proxy) are returned as-is.
 */
export const buildUrl = (config: GatewayConfig): string => {
  if (config.host.startsWith('/')) return config.host

  const protocol = config.isSecured ? 'https' : 'http'
  const port = config.port ? `:${config.port}` : ''
  return `${protocol}://${config.host}${port}`
}

const buildGatewayEndpointUrl = (baseUrl: string, route: string) => `${baseUrl}/${route}`

const fetchGatewayText = (baseUrl: string, route: string) =>
  fetchText(buildGatewayEndpointUrl(baseUrl, route)).then((value) => value.trim())

/**
 * Fetches libraries and gateway metadata from GG endpoints (no fallbacks).
 */
export const fetchLibraries = async (config: GatewayConfig): Promise<LibrariesPayload> => {
  const baseUrl = buildUrl(config)

  const libraries = await fetchJson<Library[]>(buildGatewayEndpointUrl(baseUrl, GATEWAY_ROUTE_LIBRARIES))

  const [guid, registry, projectName, gsmuEndpoint, websocketPort, mcpPort] = await Promise.all([
    fetchGatewayText(baseUrl, GATEWAY_ROUTE_GUID),
    fetchGatewayText(baseUrl, GATEWAY_ROUTE_REGISTRY),
    fetchGatewayText(baseUrl, GATEWAY_ROUTE_PROJECT),
    fetchGatewayText(baseUrl, GATEWAY_ROUTE_GSMU_ENDPOINT),
    fetchGatewayText(baseUrl, GATEWAY_ROUTE_WEBSOCKET_PORT),
    fetchGatewayText(baseUrl, GATEWAY_ROUTE_MCP_PORT),
  ])

  return { libraries, guid, registry, projectName, gsmuEndpoint, websocketPort, mcpPort }
}
